import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthService } from "services/auth.service";
import { io, Socket } from "socket.io-client";

interface ServerToClientEvents {
  connect: () => void;
  disconnect: () => void;
}

interface ServerToClientEvents {
  [event: string]: (...args: any[]) => void;
}

interface ClientToServerEvents {
  subscribe: (topic: string) => void;
  unsubscribe: (topic: string) => void;
}

const SocketContext = createContext<Socket<
  ServerToClientEvents,
  ClientToServerEvents
> | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket<
    ServerToClientEvents,
    ClientToServerEvents
  > | null>(null);

  useEffect(() => {
    const initializeSocket = async () => {
      try {
        const refreshedToken = await AuthService.getToken();

        const socketUrl = import.meta.env.REACT_APP_WEB_SOCKET_URL;
        if (!socketUrl) {
          throw new Error(
            "WebSocket URL is not defined in environment variables."
          );
        }
        const { protocol, host, pathname: path } = new URL(socketUrl);

        const newSocket = io(`${protocol}//${host}`, {
          autoConnect: true,
          reconnection: true,
          transports: ["websocket"],
          path,
          auth: {
            token: refreshedToken ? `Bearer ${refreshedToken}` : null,
          },
        });

        newSocket.on("connect_error", (error) => {
          console.error("WebSocket Connection Error:", error.message);
        });

        newSocket.on("disconnect", (reason) => {
          console.warn("WebSocket Disconnected:", reason);
        });

        newSocket.on("reconnect_attempt", (attempt) => {
          console.info(`WebSocket Reconnection Attempt #${attempt}`);
        });

        setSocket(newSocket);

        return () => {
          newSocket.disconnect();
        };
      } catch (error) {
        console.error("Error initializing WebSocket:", error);
      }
    };

    initializeSocket();
  }, []);
  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

export const useSocket = () => {
  const socket = useContext(SocketContext);
  return socket;
};
